/**
 * Service Plans — the operational playbook.
 *
 * A plan's items are what a cycle expands into. Plans are versioned rather than
 * edited: superseding creates v+1 and leaves existing engagements pointing at
 * what they were sold, so changing a package today cannot rewrite what a client
 * was owed last March.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ServiceLine, type ServicePlan, type PlanItem } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Layers, Copy, Plus, Pencil, Trash2, EyeOff } from 'lucide-react';
import { PlanFormDialog } from './plan-form-dialog';
import { PlanItemDialog } from './plan-item-dialog';

export function ServicePlansPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [openPlan, setOpenPlan] = useState<string | null>(null);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ServicePlan | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const { data: linesData } = useQuery({
    queryKey: ['svc', 'service-lines'],
    queryFn: () => api.getServiceLines(),
  });
  const { data: plansData, isLoading } = useQuery({
    queryKey: ['svc', 'plans', showInactive],
    queryFn: () => api.getServicePlans({ activeOnly: !showInactive }),
  });

  // Deletes a plan nobody is on; deactivates one an engagement has used, since
  // past work still points at its deliverables. The server decides which.
  const removePlan = useMutation({
    mutationFn: (id: string) => api.deleteServicePlan(id),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not remove', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({
        title: r.data?.outcome === 'deactivated' ? 'Plan deactivated' : 'Plan deleted',
        description: r.data?.outcome === 'deactivated'
          ? 'An engagement has used it, so it was hidden rather than deleted — its history stays intact.'
          : undefined,
      });
    },
  });

  const toggleLine = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.setServiceLineActive(id, isActive),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not update', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Service line updated' });
    },
  });

  const supersede = useMutation({
    mutationFn: (id: string) => api.supersedeServicePlan(id),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not supersede', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: `Created version ${r.data?.version ?? ''}` });
    },
  });

  const lines = (linesData?.data ?? []) as ServiceLine[];
  const plans = (plansData?.data ?? []) as ServicePlan[];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-semibold tracking-tight">Service Plans</h1>
          <p className="text-sm text-muted-foreground mt-1">
            What each package includes, and how much labour it is planned to take.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowInactive((v) => !v)}>
            <EyeOff className="h-4 w-4 mr-1.5" />
            {showInactive ? 'Hide retired' : 'Show retired'}
          </Button>
          <Button onClick={() => setCreatingPlan(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New plan
          </Button>
        </div>
      </div>

      <PlanFormDialog open={creatingPlan} onOpenChange={setCreatingPlan} />
      <PlanFormDialog
        plan={editingPlan}
        open={!!editingPlan}
        onOpenChange={(v) => !v && setEditingPlan(null)}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {lines.map((l) => (
          <Card key={l.id}>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{l.name}</span>
                <Layers className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-semibold mt-1">{l.plan_count}</p>
              <p className="text-xs text-muted-foreground">
                {l.active_engagement_count} active engagement{l.active_engagement_count === 1 ? '' : 's'}
              </p>
              <Button
                variant="ghost" size="sm"
                className="mt-2 -ml-2 h-7 text-xs text-muted-foreground"
                onClick={() => toggleLine.mutate({ id: l.id, isActive: false })}
                disabled={toggleLine.isPending || l.active_engagement_count > 0}
                title={l.active_engagement_count > 0
                  ? 'Has active engagements — end those first'
                  : 'Hide this service line from the pickers'}
              >
                <EyeOff className="h-3.5 w-3.5 mr-1.5" /> Retire
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : plans.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No plans yet. A plan defines the deliverables, their frequency and who performs them —
          it is what a cycle expands into each period.
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {plans.map((p) => (
            <Card key={p.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">
                      {p.name}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        v{p.version} · {p.service_line_name}
                      </span>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      {p.list_price != null ? `${formatCurrency(p.list_price)} / ${p.billing_frequency}` : 'no list price'}
                      {' · '}{Number(p.planned_hours).toFixed(2)}h planned per cycle
                      {' · '}{p.item_count ?? 0} deliverable{(p.item_count ?? 0) === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!p.is_active && <Badge variant="secondary">superseded</Badge>}
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setOpenPlan(openPlan === p.id ? null : p.id)}
                    >
                      {openPlan === p.id ? 'Hide' : 'View'} deliverables
                    </Button>
                    {p.is_active && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setEditingPlan(p)}
                        title={(p.active_engagement_count ?? 0) > 0
                          ? 'In use — editing is refused; create a new version instead'
                          : 'Edit this plan'}
                      >
                        <Pencil className="h-4 w-4 mr-1.5" /> Edit
                      </Button>
                    )}
                    {p.is_active && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => supersede.mutate(p.id)}
                        disabled={supersede.isPending}
                        title="Create the next version. Existing engagements keep the version they were sold."
                      >
                        <Copy className="h-4 w-4 mr-1.5" /> New version
                      </Button>
                    )}
                    {p.is_active && (
                      <Button
                        variant="ghost" size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          const used = (p.active_engagement_count ?? 0) > 0;
                          const msg = used
                            ? `"${p.name}" is in use. It will be hidden rather than deleted, so existing engagements and their history are untouched. Continue?`
                            : `Delete "${p.name}" and its deliverables? Nothing is using it, so this cannot be undone.`;
                          if (window.confirm(msg)) removePlan.mutate(p.id);
                        }}
                        disabled={removePlan.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              {openPlan === p.id && <PlanItems planId={p.id} />}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function PlanItems({ planId }: { planId: string }) {
  const [editing, setEditing] = useState<PlanItem | null>(null);
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'plan', planId],
    queryFn: () => api.getServicePlan(planId),
  });

  if (isLoading) return <CardContent className="text-sm text-muted-foreground">Loading…</CardContent>;
  const items = (data?.data?.items ?? []) as PlanItem[];

  return (
    <CardContent className="p-0 border-t">
      <PlanItemDialog planId={planId} open={adding} onOpenChange={setAdding} />
      <PlanItemDialog
        planId={planId} item={editing}
        open={!!editing} onOpenChange={(v) => !v && setEditing(null)}
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Deliverable</TableHead>
            <TableHead>Frequency</TableHead>
            <TableHead className="text-right">Est. hours</TableHead>
            <TableHead>Performed by</TableHead>
            <TableHead>Due offset</TableHead>
            <TableHead>Gates</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((i) => (
            <TableRow key={i.id}>
              <TableCell>
                <div className="font-medium">{i.name}</div>
                {i.acceptance_criteria && (
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {i.acceptance_criteria}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-sm capitalize">
                {i.frequency.replace('_', ' ')}
                {i.frequency === 'custom' && i.interval_days ? ` (${i.interval_days}d)` : ''}
              </TableCell>
              <TableCell className="text-right tabular-nums">{i.estimated_hours}</TableCell>
              <TableCell className="text-sm capitalize">{i.responsible_role}</TableCell>
              <TableCell className="text-sm">day {i.due_day_offset}</TableCell>
              <TableCell className="space-x-1">
                {!i.is_included && <Badge variant="outline">out of scope</Badge>}
                {i.requires_authorization && <Badge variant="outline">needs auth</Badge>}
                {i.requires_internal_review && <Badge variant="secondary">review</Badge>}
                {i.requires_client_approval && <Badge variant="secondary">client</Badge>}
              </TableCell>
              <TableCell>
                <Button variant="ghost" size="sm" onClick={() => setEditing(i)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="px-6 py-3 border-t">
        <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> Add deliverable
        </Button>
      </div>
    </CardContent>
  );
}
